import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';

import MapPopup, { MapPopupRef } from '../MapPopup';
import TableCellEllipsis from './TableCellEllipsis';

import { TroubleTicket } from 'types/types';
import { sortByProperties, SortOrder } from '../Helpers/generalHelpers';

import 'assets/styles/TroubleTicketsTable.scss';

interface TroubleTicketsTableProps {
  cellDetailsOpen: boolean;
  mapPopupStateVisibility: boolean;
  handleTroubleTicketsPopup: () => void;
}

export interface TroubleTicketsTableRef {
  mapPopupRef: React.RefObject<MapPopupRef>;
  handleTroubleTicketsData: React.Dispatch<
    React.SetStateAction<TroubleTicket[]>
  >;
}

const TroubleTicketsTable: React.ForwardRefRenderFunction<
  TroubleTicketsTableRef,
  TroubleTicketsTableProps
> = (
  { cellDetailsOpen, handleTroubleTicketsPopup, mapPopupStateVisibility },
  ref
) => {
  const mapPopupRef = useRef<MapPopupRef>(null);

  const [troubleTickets, setTroubleTickets] = useState<TroubleTicket[]>([]);

  const sortedData = sortByProperties(troubleTickets, [
    ['TicketNumber', SortOrder.ASCENDING],
    ['ProductIdentifier', SortOrder.ASCENDING],
    ['NIDTCode', SortOrder.ASCENDING],
    ['ClientImpact', SortOrder.ASCENDING],
    ['CommentDate', SortOrder.ASCENDING],
  ]);

  useImperativeHandle(ref, () => ({
    mapPopupRef: mapPopupRef,
    handleTroubleTicketsData: setTroubleTickets,
  }));

  return (
    <MapPopup
      ref={mapPopupRef}
      title={`Trouble Tickets`}
      cellDetailsOpen={cellDetailsOpen}
      handleClose={handleTroubleTicketsPopup}
      mapPopupStateVisibility={mapPopupStateVisibility}
    >
      {!troubleTickets ||
        (troubleTickets?.length === 0 && (
          <Box textAlign="center">No Tickets Found</Box>
        ))}
      {troubleTickets && troubleTickets.length > 0 && (
        <TableContainer className="trouble-tickets-table">
          <Box sx={{ maxHeight: '100%', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket Number</TableCell>
                  <TableCell>Product Identifier</TableCell>
                  <TableCell>NIDT Code</TableCell>
                  <TableCell>Client Impact</TableCell>
                  <TableCell style={{ width: '30rem' }}>Comment</TableCell>
                  <TableCell>Comment Date</TableCell>
                  <TableCell>Resource Type Identifier</TableCell>
                  <TableCell>Ticket Type</TableCell>
                  <TableCell>Treatment Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Ticket Closure Date</TableCell>
                  <TableCell>Ticket Start Date</TableCell>
                  <TableCell>Symptom</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Final Nature</TableCell>
                  <TableCell>Brief Description</TableCell>
                  <TableCell>Imputation Description</TableCell>
                  <TableCell>Technical Impact</TableCell>
                  <TableCell>Problem Family</TableCell>
                  <TableCell>Symptom Family</TableCell>
                  <TableCell>Ticket Restoration Date</TableCell>
                  <TableCell>Problem Details</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedData.map((el, index) => (
                  <TableRow key={index}>
                    <TableCell>{el['TicketNumber']}</TableCell>
                    <TableCell>{el['ProductIdentifier']}</TableCell>
                    <TableCell>{el['NIDTCode']}</TableCell>
                    <TableCell>{el['ClientImpact']}</TableCell>
                    <TableCell style={{ width: '30rem' }}>
                      <TableCellEllipsis value={el['Comment']} />
                    </TableCell>
                    <TableCell>{el['CommentDate'].toString()}</TableCell>
                    <TableCell>{el['ResourceTypeIdentifier']}</TableCell>
                    <TableCell>{el['TicketType']}</TableCell>
                    <TableCell>{el['TreatmentPriority']}</TableCell>
                    <TableCell>{el['Status']}</TableCell>
                    <TableCell>{el['CreationDate']}</TableCell>
                    <TableCell>{el['TicketClosureDate']}</TableCell>
                    <TableCell>{el['TicketStartDate']}</TableCell>
                    <TableCell>{el['Symptom']}</TableCell>
                    <TableCell>{el['Origin']}</TableCell>
                    <TableCell>{el['FinalNature']}</TableCell>
                    <TableCell>{el['BriefDescription']}</TableCell>
                    <TableCell>{el['ImputationDescription']}</TableCell>
                    <TableCell>{el['TechnicalImpact']}</TableCell>
                    <TableCell>{el['ProblemFamily']}</TableCell>
                    <TableCell>{el['SymptomFamily']}</TableCell>
                    <TableCell>{el['TicketRestorationDate']}</TableCell>
                    <TableCell>{el['ProblemDetails']}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}
    </MapPopup>
  );
};

export default forwardRef<TroubleTicketsTableRef, TroubleTicketsTableProps>(
  TroubleTicketsTable
);
